<template>
  <div v-if="showModal" class="ipf-fullscreen">
    <!-- Header -->
    <div class="ipf-header">
      <div class="ipf-header-left">
        <button
          class="ipf-back"
          @click.stop.prevent="handleClose"
          @touchstart="handleClose"
          aria-label="Volver"
          type="button"
        >
          <i class="el-icon-arrow-left"></i>
        </button>
        <h5 class="ipf-title" v-if="invoice">
          <template v-if="invoice._loading || !invoice.number_invoice">
            Cargando factura...
          </template>
          <template v-else>Factura N° {{ invoice.number_invoice }}</template>
        </h5>
      </div>
      <div class="ipf-header-right">
        <!-- Verificar con SRI (solo para timeout con clave de acceso) -->
        <button
          v-if="status === 'timeout' && invoice && invoice.access_key"
          class="ipf-action-btn ipf-action-btn--verify"
          @click.stop="$emit('action', 'invoice-verify')"
          title="Verificar estado en el SRI"
          type="button"
        >
          <i class="el-icon-refresh"></i>
        </button>
        <!-- Firmar / reintentar firma -->
        <button
          v-if="
            status === 'draft' || status === 'failed' || status === 'timeout'
          "
          class="ipf-action-btn"
          @click.stop="$emit('action', 'invoice-signature')"
          :title="
            status === 'timeout' ? 'Reintentar firma' : 'Firmar documento'
          "
          type="button"
        >
          <i class="el-icon-edit-outline"></i>
        </button>
        <button
          v-if="status !== 'nuled'"
          class="ipf-action-btn"
          @click="$emit('action', 'invoice-send')"
          title="Enviar"
        >
          <i class="el-icon-s-promotion"></i>
        </button>
        <button
          v-if="status === 'success'"
          class="ipf-action-btn"
          :class="{ 'ipf-action-btn--reminder-on': reminderEnabled }"
          :title="
            reminderEnabled ? 'Pausar recordatorios' : 'Activar recordatorios'
          "
          :disabled="reminderLoading"
          @click="toggleReminder"
        >
          <i class="el-icon-bell"></i>
        </button>
        <button
          class="ipf-action-btn"
          @click="$emit('action', 'invoice-download')"
          title="Descargar PDF"
        >
          <i class="el-icon-download"></i>
        </button>
        <el-dropdown trigger="click" @command="cmd => $emit('action', cmd)">
          <button class="ipf-action-btn">
            <i class="el-icon-more"></i>
          </button>
          <el-dropdown-menu slot="dropdown" class="ipf-dropdown-menu">
            <el-dropdown-item command="invoice-view-doc">
              <i class="el-icon-document"></i> Ver documento completo
            </el-dropdown-item>
            <el-dropdown-item
              v-if="status === 'success' && invoice && !invoice.is_paid"
              command="invoice-paylink"
            >
              <i class="el-icon-link"></i> Link de pago
            </el-dropdown-item>
            <el-dropdown-item
              v-if="status === 'draft' || status === 'failed'"
              command="invoice-edit"
            >
              <i class="el-icon-edit"></i> Editar
            </el-dropdown-item>
            <el-dropdown-item command="invoice-print">
              <i class="el-icon-printer"></i> Vista de impresión
            </el-dropdown-item>
            <el-dropdown-item
              v-if="status !== 'nuled' && invoice && !invoice.is_paid"
              command="invoice-add-payment"
            >
              <i class="el-icon-wallet" style="margin-right:4px;"></i> Agregar
              pago
            </el-dropdown-item>
            <el-dropdown-item
              v-if="
                status !== 'nuled' &&
                  invoice &&
                  (invoice.is_paid || invoice.payment_status === 'partial')
              "
              @click.native="editPayments"
            >
              <i class="el-icon-edit-outline" style="margin-right:4px;"></i>
              Editar pagos
            </el-dropdown-item>
            <el-dropdown-item
              v-if="status === 'success'"
              command="invoice-nuled"
              class="text-danger"
            >
              <i class="el-icon-circle-close"></i> Anular factura
            </el-dropdown-item>
            <el-dropdown-item
              v-if="status === 'nuled'"
              command="invoice-reactivate"
            >
              <i class="el-icon-refresh-right"></i> Reactivar factura
            </el-dropdown-item>
            <el-dropdown-item
              v-if="
                status === 'draft' ||
                  status === 'failed' ||
                  status === 'timeout'
              "
              command="invoice-delete"
              class="text-danger"
            >
              <i class="el-icon-delete"></i> Eliminar
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <button
          class="ipf-close"
          @click.stop.prevent="handleClose"
          @touchstart="handleClose"
          aria-label="Cerrar"
          type="button"
        >
          <i class="el-icon-close"></i>
        </button>
      </div>
    </div>

    <!-- Skeleton mientras se carga la factura -->
    <div class="ipf-body" v-if="!invoice || invoice._loading">
      <b-container fluid class="pb-4 px-lg-4">
        <div class="ipf-skeleton">
          <div class="ipf-sk-card">
            <div class="ipf-sk-row">
              <div class="ipf-sk-block ipf-sk-block--logo"></div>
              <div class="ipf-sk-stack">
                <div class="ipf-sk-line ipf-sk-line--w60"></div>
                <div class="ipf-sk-line ipf-sk-line--w40"></div>
                <div class="ipf-sk-line ipf-sk-line--w50"></div>
              </div>
            </div>
            <div class="ipf-sk-divider"></div>
            <div class="ipf-sk-grid">
              <div>
                <div
                  class="ipf-sk-line ipf-sk-line--small ipf-sk-line--w40"
                ></div>
                <div class="ipf-sk-line ipf-sk-line--w70"></div>
              </div>
              <div>
                <div
                  class="ipf-sk-line ipf-sk-line--small ipf-sk-line--w40"
                ></div>
                <div class="ipf-sk-line ipf-sk-line--w70"></div>
              </div>
              <div>
                <div
                  class="ipf-sk-line ipf-sk-line--small ipf-sk-line--w40"
                ></div>
                <div class="ipf-sk-line ipf-sk-line--w50"></div>
              </div>
            </div>
            <div class="ipf-sk-divider"></div>
            <div class="ipf-sk-table">
              <div class="ipf-sk-line ipf-sk-line--w90"></div>
              <div class="ipf-sk-line ipf-sk-line--w80"></div>
              <div class="ipf-sk-line ipf-sk-line--w85"></div>
            </div>
          </div>
        </div>
      </b-container>
    </div>

    <!-- Body scrolleable -->
    <div class="ipf-body" v-if="invoice && !invoice._loading">
      <b-container fluid class="pb-4 px-lg-4">
        <!-- Banner específico para timeout: puede estar autorizada en SRI -->
        <b-row v-if="status === 'timeout'">
          <b-col>
            <AlertInfoComponent
              title="Esta factura pudo haberse enviado al SRI"
              type="warning"
              class="mb-4 mt-4"
              :description="
                invoice.access_key
                  ? 'El sistema no recibió confirmación del SRI, pero el documento pudo haber sido procesado. Haz clic en el ícono ↺ para verificar el estado actual con el SRI antes de volver a firmar.'
                  : 'El sistema no recibió confirmación del SRI. Si en el portal sri.gob.ec aparece como autorizada, no la vuelvas a firmar. Si no aparece, usa el botón de firma para reintentarlo.'
              "
            />
          </b-col>
        </b-row>
        <b-row v-if="invoice.sri_sign_message && status !== 'timeout'">
          <b-col>
            <AlertInfoComponent
              title="Notificación SRI"
              type="error"
              class="mb-4 mt-4"
              :description="invoice.sri_sign_message"
            />
          </b-col>
        </b-row>
        <b-row v-if="invoice">
          <b-col>
            <InvoiceTimeline :invoiceId="invoice.id" class="mb-3 mt-4" />
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <div class="card box-shadow card-quotes-view py-4">
              <div class="card-body">
                <div class="card-quotes-view-header">
                  <!-- Mobile header -->
                  <DocumentViewHeader
                    class="d-block d-md-none"
                    doc-type="Factura"
                    :doc-number="invoice.number_invoice"
                    :business="{
                      logo: getUser.user_business.business_logo,
                      name: getUser.user_business.social_reason,
                      ruc: getUser.user_business.business_ruc
                    }"
                    :date="invoice.emission_date_for_view"
                    :status="invoice.status"
                    :client="{
                      name: invoice.client.name,
                      document: invoice.client.document,
                      email: invoice.client.email
                    }"
                    :access-key="invoice.access_key"
                    :sign-mode="invoice.sign_mode"
                    :meta-items="[
                      {
                        label: 'Punto de emisión',
                        value: invoice.user_business_e_point_id
                          ? invoice.emission_point.code
                          : null
                      },
                      {
                        label: 'Forma de pago',
                        value: invoice.sri_forms_payment
                          ? invoice.sri_forms_payment.title
                          : null
                      }
                    ]"
                  />

                  <!-- Desktop header -->
                  <b-row class="d-none d-md-flex">
                    <b-col sm="12" md="12" lg="6">
                      <div class="card-quotes-view-logo mb-4">
                        <b-row>
                          <b-col sm="12" md="5" lg="4">
                            <div class="logo">
                              <img
                                v-if="getUser.user_business.business_logo"
                                :src="getUser.user_business.business_logo"
                                class="image"
                                alt="Empresa"
                                style="width: 100%; max-width: 200px; height: auto;"
                              />
                            </div>
                          </b-col>
                          <b-col sm="12" md="7" lg="8">
                            <div class="info">
                              <h4 class="h4 font-bold m-0">
                                N° {{ invoice.number_invoice }}
                              </h4>
                              <h2 class="h2 font-bold m-0">
                                {{ getUser.user_business.social_reason }}
                              </h2>
                              <p class="font-semi-bold mt-2">
                                RUC: {{ getUser.user_business.business_ruc }}
                              </p>
                              <p class="font-semi-bold">
                                {{ getUser.user_business.business_address }}
                              </p>
                              <SignAuthorizationInfo
                                v-if="invoice.access_key"
                                class="mt-3"
                                :accessKey="invoice.access_key"
                                :signMode="invoice.sign_mode"
                              />
                            </div>
                          </b-col>
                        </b-row>
                      </div>
                    </b-col>
                    <b-col sm="12" md="12" lg="6">
                      <div class="card-quotes-view-info">
                        <b-row>
                          <b-col sm="4" md="4" lg="4">
                            <label class="font-semi-bold d-block"
                              >Emisión</label
                            >
                            <p class="m-0">
                              {{ invoice.emission_date_for_view }}
                            </p>
                          </b-col>
                          <b-col sm="4" md="4" lg="4">
                            <label class="font-semi-bold d-block"
                              >Punto de emisión</label
                            >
                            <p
                              class="m-0"
                              v-if="invoice.user_business_e_point_id"
                            >
                              {{ invoice.emission_point.code }}
                            </p>
                          </b-col>
                          <b-col sm="4" md="4" lg="4">
                            <label class="font-semi-bold d-block mb-2"
                              >Status</label
                            >
                            <StatusReadDocumentsComponent
                              :status="invoice.status"
                            />
                          </b-col>
                        </b-row>
                        <b-row class="mt-4">
                          <b-col sm="4" md="4" lg="4">
                            <label class="font-semi-bold d-block"
                              >Cliente</label
                            >
                            <p class="m-0">{{ invoice.client.name }}</p>
                          </b-col>
                          <b-col sm="4" md="4" lg="4">
                            <label class="font-semi-bold d-block"
                              >Documento</label
                            >
                            <p class="m-0">{{ invoice.client.document }}</p>
                          </b-col>
                          <b-col sm="4" md="4" lg="4">
                            <label class="font-semi-bold d-block">Correo</label>
                            <p class="m-0 text-break">
                              {{ invoice.client.email }}
                            </p>
                          </b-col>
                        </b-row>
                      </div>
                    </b-col>
                  </b-row>
                </div>

                <div class="card-quotes-view-body mt-5">
                  <div class="card-quotes-view-body-products">
                    <!-- Desktop tabla -->
                    <div class="d-none d-md-block">
                      <div class="card-quotes-view-table table-items">
                        <b-row class="table-items-header">
                          <b-col md="1">
                            <h6 class="h6 font-bold mb-0 ml-2">Código</h6>
                          </b-col>
                          <b-col md="5">
                            <h6 class="h6 font-bold mb-0">Detalle</h6>
                          </b-col>
                          <b-col md="2">
                            <h6 class="h6 font-bold mb-0">Cantidad</h6>
                          </b-col>
                          <b-col md="2">
                            <h6 class="h6 font-bold mb-0">Precio U.</h6>
                          </b-col>
                          <b-col md="2">
                            <h6 class="h6 font-bold mb-0">Subtotal</h6>
                          </b-col>
                        </b-row>
                      </div>
                      <b-row
                        class="mt-4"
                        v-for="(product, index) in invoice.products"
                        :key="'d' + index"
                      >
                        <b-col md="1">
                          <p class="m-0 ml-2">
                            {{ product.product_code || product.product.code }}
                          </p>
                        </b-col>
                        <b-col md="5">
                          <p class="m-0">
                            <strong>{{ product.product.name }}</strong>
                            <span
                              v-if="product.product_detail"
                              v-html="product.product_detail"
                              class="text-pre-wrap d-block mt-1 text-muted"
                            ></span>
                          </p>
                        </b-col>
                        <b-col md="2">
                          <p class="m-0">{{ product.product_quantity }}</p>
                        </b-col>
                        <b-col md="2">
                          <p class="m-0">${{ product.product_amount }}</p>
                        </b-col>
                        <b-col md="2">
                          <p class="m-0">
                            ${{
                              handleCalculateSubTotal(
                                product.product_quantity,
                                product.product_amount
                              )
                            }}
                          </p>
                        </b-col>
                      </b-row>
                    </div>

                    <!-- Mobile: tarjetas por ítem -->
                    <div class="d-block d-md-none doc-mobile-items">
                      <div
                        v-for="(product, index) in invoice.products"
                        :key="'m' + index"
                        class="doc-item"
                      >
                        <div class="doc-item-row">
                          <span class="doc-item-name">{{
                            product.product.name
                          }}</span>
                          <span class="doc-item-total">
                            ${{
                              handleCalculateSubTotal(
                                product.product_quantity,
                                product.product_amount
                              )
                            }}
                          </span>
                        </div>
                        <div v-if="product.product.code" class="doc-item-sku">
                          SKU: {{ product.product.code }}
                        </div>
                        <div class="doc-item-qty">
                          Cant: {{ product.product_quantity }} × ${{
                            product.product_amount
                          }}
                        </div>
                        <div
                          v-if="product.product_detail"
                          v-html="product.product_detail"
                          class="doc-item-detail text-pre-wrap"
                        ></div>
                      </div>
                    </div>
                  </div>
                  <hr />
                  <b-row>
                    <b-col xs="12" sm="12" md="6">
                      <div
                        class="card-quotes-view-method-pay mt-4 p-4"
                        v-if="invoice.sri_forms_payment"
                      >
                        <p class="font-semi-bold mb-2">Forma de pago</p>
                        <p class="m-0">
                          {{ invoice.sri_forms_payment.title }}
                        </p>
                      </div>
                      <div
                        class="card-quotes-view-aditionals bg-color-gray p-4 mt-4"
                        v-if="
                          invoice.additional_extras &&
                            invoice.additional_extras.length
                        "
                      >
                        <p class="font-semi-bold">Información adicional</p>
                        <b-row
                          class="my-2"
                          v-for="(additional,
                          index) in invoice.additional_extras"
                          :key="index"
                        >
                          <b-col
                            >{{ additional.name }} -
                            {{ additional.description }}</b-col
                          >
                        </b-row>
                      </div>
                    </b-col>
                    <b-col xs="12" sm="12" md="4" offset-md="2">
                      <DocumentCalculator
                        readOnly
                        :setProducts="invoice.products"
                        :setDisc="invoice.discount"
                        :setServ="invoice.service"
                      />
                    </b-col>
                  </b-row>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </div>
</template>

<script>
import StatusReadDocumentsComponent from "../../../../components/StatusReadDocuments";
import DocumentCalculator from "../../../components/DocumentCalculator";
import DocumentViewHeader from "../../../components/DocumentViewHeader";
import SignAuthorizationInfo from "../../../components/SignAuthorizationInfo";
import AlertInfoComponent from "@/components/Alerts/Info";
import InvoiceTimeline from "./InvoiceTimeline.vue";
import { parseFloatFormat } from "@/assets/utils/format";
import invoiceService from "../services/invoiceService";

export default {
  name: "InvoicePreviewComponent",
  data() {
    return {
      reminderLoading: false,
      // Estado local para feedback inmediato tras toggle (null = usar prop)
      localReminderEnabled: null
    };
  },
  props: {
    invoice: {
      required: true
    },
    showModal: {
      type: Boolean,
      required: true
    },
    status: {
      type: String,
      default: ""
    }
  },
  // Nota: anteriormente este componente hacía
  // `document.body.appendChild(this.$el)` en mounted() para escapar de
  // containing blocks con transform/filter en ancestros. ESO ROMPÍA los
  // event handlers: cuando este componente se renderiza desde varios padres
  // (DashboardLayout + ProjectDetail + CollectionsTab + LinkInvoiceModal),
  // las instancias migradas al body se desconectaban del ciclo reactivo de
  // Vue → ningún botón del header respondía. Confiamos en
  // `position: fixed; z-index: 2100` que es suficiente para navegadores
  // modernos sin ancestros que rompan el viewport positioning.
  watch: {
    showModal: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          document.addEventListener("keydown", this.onEscape);
          document.body.style.overflow = "hidden";
          this.toggleThirdPartyWidgets(true);
        } else {
          document.removeEventListener("keydown", this.onEscape);
          document.body.style.overflow = "";
          this.toggleThirdPartyWidgets(false);
        }
      }
    },
    // Si el padre recarga la factura, descartamos el estado local
    invoice() {
      this.localReminderEnabled = null;
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEscape);
    document.body.style.overflow = "";
    if (typeof this.toggleThirdPartyWidgets === "function") {
      this.toggleThirdPartyWidgets(false);
    }
    // Nota: ya no necesitamos removeChild de document.body — el componente
    // se monta en su parent natural y Vue lo limpia al destruir.
  },
  methods: {
    handleClose() {
      this.$emit("close");
    },
    editPayments() {
      // Abre el modal global de pagos directo en la gestión (editar/eliminar).
      this.$root.$emit("open-payment-modal", {
        invoice: this.invoice,
        tab: "partial"
      });
    },
    async toggleReminder() {
      if (this.reminderLoading || !this.invoice) return;
      this.reminderLoading = true;
      const enable = !this.reminderEnabled;
      try {
        const res = await invoiceService.updateReminders(this.invoice.id, {
          enable_reminders: enable
        });
        if (res && res.data && res.data.success) {
          this.localReminderEnabled = enable;
          this.$emit("reminder-updated");
          this.$notifications.success(
            enable ? "Recordatorios activados" : "Recordatorios pausados"
          );
        } else {
          this.$notifications.error(
            (res && res.data && res.data.message) ||
              "No se pudo actualizar los recordatorios"
          );
        }
      } finally {
        this.reminderLoading = false;
      }
    },
    onEscape(e) {
      if (e.key === "Escape") this.handleClose();
    },
    toggleThirdPartyWidgets(hide) {
      const selectors = [".amo-button-holder", ".signing-toast-queue"];
      selectors.forEach(sel => {
        const el = document.querySelector(sel);
        if (el) el.style.display = hide ? "none" : "";
      });
    },
    handleCalculateSubTotal(quantity, price) {
      return parseFloatFormat(quantity * price);
    }
  },
  computed: {
    getUser() {
      return this.$store.getters.getUser;
    },
    reminderEnabled() {
      if (this.localReminderEnabled !== null) return this.localReminderEnabled;
      if (!this.invoice) return false;
      return !!this.invoice.enable_reminders;
    }
  },
  components: {
    StatusReadDocumentsComponent,
    DocumentCalculator,
    DocumentViewHeader,
    SignAuthorizationInfo,
    AlertInfoComponent,
    InvoiceTimeline
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$border: #e4eaf6;

.ipf-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 2100;
  background: #f5f6fa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ipf-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
  background: #fff;
}
.ipf-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ipf-title {
  font-size: 15px;
  font-weight: 600;
  color: $primary;
  margin: 0;
}
.ipf-back {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: $primary;
  transition: background 0.15s;
  &:hover {
    background: #f0f2f7;
  }
}
.ipf-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ipf-action-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: $primary;
  transition: all 0.15s;
  &:hover {
    background: #f0f2f7;
    color: darken($primary, 10%);
  }
}
.ipf-action-btn--reminder-on {
  color: #3ecfb4 !important;
  &:hover {
    background: #f0fdfb !important;
    color: darken(#3ecfb4, 8%) !important;
  }
}
.ipf-action-btn--verify {
  color: #e6a817 !important;
  &:hover {
    background: #fffbf0 !important;
    color: darken(#e6a817, 10%) !important;
  }
}

.ipf-btn-sign {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 10px;
  background: $primary;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
  &:hover {
    background: darken($primary, 8%);
  }
  i {
    font-size: 14px;
  }
}
.ipf-close {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #909399;
  transition: all 0.15s;
  &:hover {
    background: #f0f2f7;
    color: $primary;
  }
}

.ipf-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;
  -webkit-overflow-scrolling: touch;
  /* Permitir selección de texto en todo el contenido del preview */
  user-select: text;
  -webkit-user-select: text;
}

// ── Skeleton loader ──
.ipf-skeleton {
  margin-top: 16px;
}
.ipf-sk-card {
  background: #fff;
  border: 1px solid #e8ecf2;
  border-radius: 10px;
  padding: 24px;
}
.ipf-sk-row {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 16px;
}
.ipf-sk-stack {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ipf-sk-block {
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f2f5 25%, #e8eaed 50%, #f0f2f5 75%);
  background-size: 200% 100%;
  animation: ipfSkShimmer 1.4s infinite;

  &--logo {
    width: 140px;
    height: 60px;
    flex-shrink: 0;
  }
}
.ipf-sk-line {
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f2f5 25%, #e8eaed 50%, #f0f2f5 75%);
  background-size: 200% 100%;
  animation: ipfSkShimmer 1.4s infinite;

  &--small {
    height: 9px;
  }
  &--w40 {
    width: 40%;
  }
  &--w50 {
    width: 50%;
  }
  &--w60 {
    width: 60%;
  }
  &--w70 {
    width: 70%;
  }
  &--w80 {
    width: 80%;
  }
  &--w85 {
    width: 85%;
  }
  &--w90 {
    width: 90%;
  }
}
.ipf-sk-divider {
  height: 1px;
  background: #e8ecf2;
  margin: 18px 0;
}
.ipf-sk-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  > div {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
}
.ipf-sk-table {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}
@keyframes ipfSkShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
@media (max-width: 768px) {
  .ipf-sk-grid {
    grid-template-columns: 1fr 1fr;
  }
  .ipf-sk-row {
    flex-direction: column;
  }
}

// Logo
.ipf-body .logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  img {
    max-width: 180px;
    height: auto;
    display: block;
    border-radius: 8px;
  }
}

// ═══ Jerarquía visual — datos principales oscuros/bold, labels sutiles ═══

// Títulos del negocio
.ipf-body .info {
  h2 {
    color: $primary;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
  }
  h4 {
    color: $primary;
    font-size: 14px;
    font-weight: 600;
  }
  // RUC, dirección — secundario
  p.font-semi-bold {
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
  }
}

// Section dividers
.ipf-body .card-quotes-view-logo {
  padding-bottom: 20px;
  border-bottom: 1px solid #e8ecf1;
}
// Labels y valores — idéntico a InvoiceView (.dvh-desktop-label / .dvh-desktop-value)
.ipf-body .card-quotes-view-info {
  padding: 16px 0;
  border-bottom: 1px solid #e8ecf1;
  label,
  label.font-semi-bold {
    font-size: 11px !important;
    color: #9ca3af !important;
    display: block;
    font-weight: 400 !important;
    text-transform: none !important;
    letter-spacing: normal !important;
    margin-bottom: 2px;
  }
  p,
  p.m-0 {
    color: $primary;
    font-weight: 600;
    font-size: 14px;
  }
}

// Table headers — hereda estilos de main.scss (.table-items-header)
// No se sobreescribe, el global ya tiene: uppercase, 11px, #6b7280, border-bottom 2px

// Rows de productos — nombre bold oscuro, detalle gris
.ipf-body .card-quotes-view-body-products {
  p {
    color: $primary;
    font-size: 13px;
  }
  strong {
    color: $primary;
    font-weight: 700;
    font-size: 14px;
  }
  .text-muted {
    color: #6b7280 !important;
    font-size: 12px;
  }
}

// Info adicional box
.ipf-body .card-quotes-view-aditionals {
  background: #f9fafb !important;
  border: 1px solid #e8ecf2;
  border-radius: 10px;

  p.font-semi-bold {
    color: $primary;
    font-size: 13px;
    font-weight: 600;
  }
}

// Payment method box
.ipf-body .card-quotes-view-method-pay {
  background: #fff;
  border: 1px solid #e8ecf2;
  border-radius: 10px;

  p.font-semi-bold {
    color: $primary;
    font-weight: 600;
    font-size: 13px;
  }
  p.m-0:not(.font-semi-bold) {
    color: $primary;
    font-weight: 600;
    font-size: 14px;
  }
}
</style>

<style lang="scss">
.ipf-dropdown-menu {
  z-index: 2200 !important;

  .el-dropdown-menu__item {
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      width: 16px;
      text-align: center;
      font-size: 15px;
    }
  }
}
</style>
